import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import API_BASE_URL from '../config/api';
import { auth, db, storage } from '../firebase';

const getFileExtension = (file) => {
  const name = String(file?.name || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  const ext = name.slice(idx).toLowerCase();
  if (!ext || ext.length > 10) return '';
  return ext;
};

const sanitizeTags = (tags, type) =>
  (Array.isArray(tags) ? tags : [])
    .map((tag) => {
      const id = String(tag?.id ?? tag?.refId ?? tag?.mountId ?? tag?.gearId ?? '').trim();
      const name = String(tag?.name ?? tag?.label ?? tag?.mountName ?? tag?.title ?? '').trim();
      if (!id || !name) {
        return null;
      }
      return { id, name, type };
    })
    .filter(Boolean);

export async function uploadImage(file, authToken) {
  if (!file) {
    throw new Error('A file is required for upload.');
  }

  // Direct-to-Storage upload (avoids multipart truncation issues through the API).
  const uid = auth?.currentUser?.uid;
  if (!uid) {
    throw new Error('Authentication is required to upload images.');
  }

  const extension = getFileExtension(file);
  const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}${extension}`;
  const storagePath = `posts/${uid}/${filename}`;
  const fileRef = ref(storage, storagePath);

  await uploadBytes(fileRef, file, {
    contentType: file.type || undefined
  });
  const downloadUrl = await getDownloadURL(fileRef);

  return { downloadUrl, storagePath };
}

export async function createPost({
  userId,
  postText,
  imageUrl = '',
  imagePath = '',
  mountainTags = [],
  gearTags = [],
  username = '',
  role = 'user',
  pfp = ''
} = {}) {
  const trimmedText = (postText || '').trim();
  if (!userId) {
    throw new Error('User ID is required to create a post.');
  }
  if (!trimmedText) {
    throw new Error('Post text cannot be empty.');
  }

  const payload = {
    userId,
    postText: trimmedText,
    imageUrl: imageUrl || '',
    imagePath: imagePath || '',
    mountainTags: sanitizeTags(mountainTags, 'mountain'),
    gearTags: sanitizeTags(gearTags, 'gear'),
    username: username || null,
    authorPfp: pfp || '',
    timestamp: serverTimestamp(),
    likes: {}
  };

  const postsCollection = collection(db, 'posts');
  const docRef = await addDoc(postsCollection, payload);
  return { id: docRef.id, ...payload };
}

export async function deletePost(postId, imagePath = '') {
  if (!postId) {
    throw new Error('Post ID is required to delete a post.');
  }

  await deleteDoc(doc(db, 'posts', postId));

  if (imagePath) {
    const fileRef = ref(storage, imagePath);
    try {
      await deleteObject(fileRef);
    } catch (error) {
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  }
}

export async function likePost(postId, userId) {
  if (!postId || !userId) {
    throw new Error('Post ID and user ID are required to like a post.');
  }

  await updateDoc(doc(db, 'posts', postId), {
    [`likes.${userId}`]: serverTimestamp()
  });
}

export async function unlikePost(postId, userId) {
  if (!postId || !userId) {
    throw new Error('Post ID and user ID are required to unlike a post.');
  }

  await updateDoc(doc(db, 'posts', postId), {
    [`likes.${userId}`]: deleteField()
  });
}
